import { LocalizedCurrency, LocalizedPercentage } from '@mews/framework';
import { Dash, TooltippedSpan } from '@mews-commander/ui';
import { TaxRateStrategyDiscriminator } from '@mews-commander/types';
import { Position } from '@optimus-web/common';
import { Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { EnrichedTaxValues } from '../../../utils/mapEntryWithEnrichedTaxes';

interface LocalProductTaxCellProps {
    data: EnrichedTaxValues[];
}

export const LocalProductTaxCell = ({ data }: LocalProductTaxCellProps) => {
    if (data.length === 0) {
        return <Dash />;
    }

    const { summedPercentageRates, summedFlatRates } = data.reduce<{
        summedPercentageRates: number | undefined;
        summedFlatRates?: { value: number; currencyCode: string };
    }>(
        (acc, { taxStrategy }) => {
            const value = taxStrategy?.value?.value ?? 0;

            if (
                [TaxRateStrategyDiscriminator.Relative, TaxRateStrategyDiscriminator.Dependent].includes(
                    taxStrategy.discriminator
                )
            ) {
                acc.summedPercentageRates =
                    acc.summedPercentageRates === undefined ? value : acc.summedPercentageRates + value;
            } else if (taxStrategy?.discriminator === TaxRateStrategyDiscriminator.Flat) {
                if (!acc.summedFlatRates) {
                    acc.summedFlatRates = { value: 0, currencyCode: taxStrategy?.value.currencyCode };
                }
                acc.summedFlatRates.value =
                    acc.summedFlatRates.value === undefined ? value : acc.summedFlatRates.value + value;
            }

            return acc;
        },
        // init with undefined for percentage rates and empty object for flat rates
        { summedPercentageRates: undefined, summedFlatRates: undefined }
    );

    return (
        <TooltippedSpan
            tooltip={data.map(({ localName, name, taxStrategy, taxRateCode }) => (
                <Typography key={taxRateCode} textStyle="bodySmallStrong" align="left" variant="inverse" as="div">
                    {localName || name}
                    {UnicodeEntity.Space}
                    {taxStrategy?.discriminator === TaxRateStrategyDiscriminator.Flat ? (
                        <LocalizedCurrency
                            value={taxStrategy?.value.value}
                            currency={taxStrategy?.value.currencyCode}
                        />
                    ) : (
                        <LocalizedPercentage value={taxStrategy?.value.value || 0} />
                    )}
                </Typography>
            ))}
            maxWidth={1000}
            tooltipPosition={Position.Top}
        >
            {summedFlatRates && (
                <LocalizedCurrency value={summedFlatRates.value} currency={summedFlatRates.currencyCode} />
            )}
            {summedFlatRates && summedPercentageRates && (
                <>
                    {UnicodeEntity.Colon}
                    {UnicodeEntity.Space}
                </>
            )}
            {summedPercentageRates && <LocalizedPercentage value={summedPercentageRates} />}
        </TooltippedSpan>
    );
};
